import { Icon } from '@mdvip/shared/components/blocks/Icon';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalDescription,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@mdvip/shared/components/blocks/ResponsiveModal';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Button } from '@mdvip/shared/components/ui/button';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { useTranslation } from 'react-i18next';
import { AwpDocumentRow } from './AwpDocumentRow';
import type { AwpDocument } from './types';
import { type DocumentDownloadError, useDocumentDownload } from './useDocumentDownload';

export interface AwpDocumentsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Shown in the order given; never re-sorted. */
  documents: AwpDocument[] | undefined;
  /** An empty list is `ready` with `[]`. */
  status: 'loading' | 'error' | 'ready';
  onRetry: () => void;
  getDownloadUrl: (doc: AwpDocument) => Promise<string>;
  /** Called when a download comes back `NOT_FOUND`, so the app can refetch its list. */
  onDocumentGone?: () => void;
  description?: string;
}

/**
 * A patient's AWP visits as one scrolling list, each row opening that visit's PDF in a new tab. The
 * app supplies the documents, their load status and how to get a download URL; the modal owns the
 * list states and the download itself.
 *
 * Download failures are an inline Alert rather than a toast: the toaster mounts outside the dialog's
 * focus trap, so a toast's Retry could not be reached by keyboard while the modal is open.
 */
export function AwpDocumentsModal({
  open,
  onOpenChange,
  documents,
  status,
  onRetry,
  getDownloadUrl,
  onDocumentGone,
  description,
}: AwpDocumentsModalProps) {
  const { t } = useTranslation('shared');
  const download = useDocumentDownload({ getDownloadUrl, onDocumentGone });

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      download.dismissError();
    }
    onOpenChange(next);
  };

  let list = null;
  if (status === 'error') {
    list = (
      <Alert variant="destructive">
        <Icon name="alertTriangle" weight="solid" />
        <AlertTitle>{t('awpDocuments.loadError.title')}</AlertTitle>
        <AlertDescription>
          <p>{t('awpDocuments.loadError.body')}</p>
          <Button variant="outline" size="sm" className="mt-3 min-h-11" onClick={onRetry}>
            <Icon name="refreshCw" size="xs" />
            {t('awpDocuments.loadError.retry')}
          </Button>
        </AlertDescription>
      </Alert>
    );
  } else if (status === 'loading') {
    list = <AwpDocumentsSkeleton label={t('awpDocuments.loading')} />;
  } else if (!documents || documents.length === 0) {
    list = (
      <div className="flex flex-col items-center rounded-2xl border-2 border-dashed border-border-strong px-6 py-10 text-center">
        <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Icon name="filePdf" size="xl" weight="solid" />
        </div>
        <p className="text-sm font-semibold text-foreground">{t('awpDocuments.empty.member.title')}</p>
        <p className="mt-1 max-w-xs text-pretty text-sm text-muted-foreground">{t('awpDocuments.empty.member.body')}</p>
      </div>
    );
  } else {
    list = (
      <ul className="flex flex-col gap-2">
        {documents.map((doc) => (
          <AwpDocumentRow
            key={doc.id}
            document={doc}
            pending={download.pendingIds.has(doc.id)}
            onDownload={download.download}
          />
        ))}
      </ul>
    );
  }

  return (
    <ResponsiveModal open={open} onOpenChange={handleOpenChange} className="sm:max-h-[calc(100dvh-200px)] sm:max-w-2xl">
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>{t('awpDocuments.title')}</ResponsiveModalTitle>
        {description && <ResponsiveModalDescription>{description}</ResponsiveModalDescription>}
      </ResponsiveModalHeader>
      <ResponsiveModalBody className="flex flex-col gap-4">
        {download.error && <DownloadErrorAlert error={download.error} onRetry={download.retry} />}
        <div>
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {t('awpDocuments.listHeading')}
          </h3>
          {list}
        </div>
      </ResponsiveModalBody>
    </ResponsiveModal>
  );
}

function DownloadErrorAlert({ error, onRetry }: { error: DocumentDownloadError; onRetry: () => void }) {
  const { t } = useTranslation('shared');
  const tError = useErrorTranslation();
  const gone = error.code === 'NOT_FOUND';

  return (
    <Alert variant="destructive">
      <Icon name="alertTriangle" weight="solid" />
      <AlertTitle>{t('awpDocuments.downloadError.title')}</AlertTitle>
      <AlertDescription>
        <p>{tError(gone ? 'AWP_DOCUMENT_NOT_FOUND' : error.code)}</p>
        {!gone && (
          <Button variant="outline" size="sm" className="mt-3 min-h-11" onClick={onRetry}>
            <Icon name="refreshCw" size="xs" />
            {t('awpDocuments.downloadError.retry')}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

/** Rows at the document row's own measurements, so nothing jumps when the list lands. */
function AwpDocumentsSkeleton({ label }: { label: string }) {
  return (
    <>
      {/* Outside the busy list: assistive tech may hold back announcements from inside an aria-busy region. */}
      <span className="sr-only" role="status">
        {label}
      </span>
      <ul className="flex flex-col gap-2" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <li key={i} className="flex items-center gap-3 rounded-lg border p-3">
            <Skeleton className="size-9 shrink-0 rounded-md" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-40" />
            </div>
            <Skeleton className="size-11 shrink-0 rounded-full" />
          </li>
        ))}
      </ul>
    </>
  );
}
